import { Select } from "@omnitok/ui";
import { decodeRange, encodeRange, inputScale } from "@pervasivemind/dashboard-render";
import { useEffect, useState } from "react";

import { IconPicker } from "@/components/IconPicker";
import { useT } from "@/i18n";
import { resolvePrimaryTable } from "@/lib/primary-table";
import { changeSelectionMode, type SelectionMode } from "@/lib/filter-selection-mode";
import { request } from "@/lib/api";
import { type DatePreset, type DisplayLabel } from "@pervasivemind/dashboard-render";
import { BilingualInput } from "@/pages/builder/components/BilingualInput";
import { DatePresetEditor } from "@/pages/builder/components/DatePresetEditor";
import { Field } from "@/pages/builder/components/Field";
import { SynonymsEditor } from "@/pages/builder/components/SynonymsEditor";
import { StructuralRow } from "@/pages/builder/inspector/StructuralRow";
import type { UseBuilderDraft } from "@/hooks/useBuilderDraft";
import type { DashboardFilter, Synonyms } from "@/types";

interface DashboardInspectorProps {
  draft: UseBuilderDraft;
}

/**
 * Read-only inspector for the dashboard root (no item/section
 * selected).
 *
 * Editable: report ``display_name`` + ``description`` (bilingual) +
 * per-filter ``label`` + ``synonyms``. Read-only: report ``key``, its
 * primary table (``resolvePrimaryTable``), filter ``key`` / ``column``
 * / ``type`` etc.
 *
 * Iter 21 — replaces ``GlobalFiltersPanel``. Structural changes
 * (adding new filters, changing the table, etc.) flow through
 * Claude via the report-builder skill.
 */
export function DashboardInspector({
  draft,
}: DashboardInspectorProps): JSX.Element {
  const { t } = useT();
  const report = draft.report;
  const filters = draft.dashboard.global_filters ?? [];

  return (
    <div className="space-y-5">
      <SectionLabel text={t("inspector.dashboard.identityEditable")} />
      <Field label={t("editor.report.displayNameLabel")}>
        <BilingualInput
          value={(report.display_name as DisplayLabel) ?? ""}
          onChange={(next) => draft.setReport({ display_name: next })}
        />
      </Field>
      <Field label={t("editor.report.descriptionLabel")}>
        <BilingualInput
          value={(report.description as DisplayLabel) ?? ""}
          onChange={(next) => draft.setReport({ description: next })}
        />
      </Field>
      {/* PR-1 (decisión D9): icono por reporte — se persiste en
          ``report_config.icon``; el portal ya resuelve ese string. */}
      <Field label={t("editor.report.iconLabel")}>
        <IconPicker
          value={report.icon ?? ""}
          onChange={(next) => draft.setReport({ icon: next })}
        />
      </Field>

      <SectionLabel
        text={t("inspector.dashboard.filtersLayout")}
      />
      <FiltersLayoutField draft={draft} />
      <ReportSnapshot reportKey={draft.key} />

      <SectionLabel text={t("inspector.dashboard.identityStructural")} />
      <StructuralRow label={t("inspector.dashboard.key")} value={draft.key} mono />
      <StructuralRow
        label={t("inspector.dashboard.duckdbTable")}
        // The table the engine queries: a declared source wins over the
        // legacy ``duckdb_table`` field. An empty answer renders as "—"
        // (StructuralRow's own empty state).
        value={resolvePrimaryTable(report)}
        mono
      />
      {report.prompt_language && (
        <StructuralRow
          label={t("inspector.dashboard.promptLanguage")}
          value={report.prompt_language}
        />
      )}

      <SectionLabel
        text={t("inspector.dashboard.globalFilters", { count: filters.length })}
      />
      {filters.length === 0 && (
        <p className="text-xs text-neutral-500">
          {t("inspector.dashboard.noFilters")}
        </p>
      )}
      <ul className="space-y-3">
        {filters.map((filter, idx) => (
          <FilterCard
            key={filter.key || idx}
            filter={filter}
            preceding={filters.slice(0, idx)}
            onChange={(diff) => draft.updateFilter(null, filter.key, diff)}
          />
        ))}
      </ul>

      <AdvancedPanel draft={draft} />
    </div>
  );
}

/**
 * Iter 26 — collapsible "Advanced" section exposing report-level
 * fields that used to be invisible to admins (set only via
 * ``seed_production.py``). For now: ``runtime_view_sql``. Future
 * extensions: cache hints, engine tuning knobs, etc.
 *
 * The textarea is plain (no Monaco) to keep the bundle slim — the
 * runtime_view_sql is read+written infrequently, syntax highlighting
 * isn't worth the ~500 KB of editor weight.
 *
 * Save flow: changes go through ``draft.replaceDashboard`` like any
 * other dashboard-level field; the SQL survives the merge thanks to
 * ``SYSTEM_MANAGED_DASHBOARD_FIELDS`` in ``builder/service.py``.
 */
function AdvancedPanel({ draft }: { draft: UseBuilderDraft }): JSX.Element {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const rvsql = draft.dashboard.runtime_view_sql ?? "";

  return (
    <div className="mt-6 border-t border-neutral-200 pt-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-left"
      >
        <span className="text-xs font-semibold text-neutral-600">
          {t("inspector.dashboard.advanced")}
        </span>
        <span className="text-xs text-neutral-500">{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          <Field
            label={
              t("inspector.dashboard.runtimeViewSql")
            }
          >
            <textarea
              value={rvsql}
              onChange={(e) =>
                draft.setDashboard({ runtime_view_sql: e.target.value })
              }
              rows={12}
              className="w-full rounded-md border border-neutral-300 bg-white p-2 font-mono text-xs leading-tight text-neutral-800 focus:border-primary focus:outline-none"
              placeholder={
                t("inspector.dashboard.runtimeViewSqlPlaceholder")
              }
            />
          </Field>
          <p className="text-xs text-neutral-500">
            {t("inspector.dashboard.runtimeViewSqlHint")}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * The per-report filter-header choice ("El Cajón").
 *
 * ``"bar"`` (default) keeps the classic embedded banner filter bar;
 * ``"cajon"`` swaps the header for the chips row + drawer. The portal
 * reads ``dashboard_config.filters_layout`` to decide which to mount.
 * Written through ``draft.setDashboard`` like any dashboard-level field;
 * it round-trips verbatim because ``promote_to_v2`` carries it on read.
 */
function FiltersLayoutField({ draft }: { draft: UseBuilderDraft }): JSX.Element {
  const { t } = useT();
  const raw = draft.dashboard.filters_layout;
  const value = raw === "cajon" || raw === "rows" ? raw : "bar";
  const options: ReadonlyArray<{ id: "bar" | "cajon" | "rows"; label: string }> = [
    { id: "bar", label: t("inspector.dashboard.filtersLayoutBar") },
    {
      id: "cajon",
      label: t("inspector.dashboard.filtersLayoutCajon"),
    },
    // Labelled rows + view toolbar (DSA 1.6): filters that share a
    // ``group`` sit on one row with the group as its caption.
    { id: "rows", label: t("inspector.dashboard.filtersLayoutRows") },
  ];

  return (
    <div className="space-y-2">
      <div className="inline-flex rounded-md border border-neutral-300 p-0.5">
        {options.map((opt) => {
          const active = value === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              aria-pressed={active}
              onClick={() => draft.setDashboard({ filters_layout: opt.id })}
              className={
                "rounded px-3 py-1 text-xs font-medium transition-colors " +
                (active
                  ? "bg-primary text-white"
                  : "text-neutral-600 hover:bg-neutral-100")
              }
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-neutral-500">
        {value === "cajon"
          ? t("inspector.dashboard.filtersLayoutCajonHint")
          : t("inspector.dashboard.filtersLayoutBarHint")}
      </p>
    </div>
  );
}

interface FilterCardProps {
  filter: DashboardFilter;
  preceding: DashboardFilter[];
  onChange: (diff: Partial<DashboardFilter>) => void;
}

export function FilterCard({ filter, preceding, onChange }: FilterCardProps): JSX.Element {
  const { t } = useT();
  const [modeError, setModeError] = useState(false);
  const supportsSelection = ['select', 'autocomplete', 'multi_select'].includes(filter.type ?? 'select');

  return (
    <li className="rounded-md border border-neutral-200 bg-neutral-50 p-2">
      <div className="mb-2 flex items-center gap-2 text-xs text-neutral-500">
        <span className="font-mono">{filter.key || "(unnamed)"}</span>
        <span className="rounded bg-neutral-200 px-1.5 py-0.5 text-neutral-600">
          {filter.type ?? "select"}
        </span>
        {filter.required && (
          <span className="rounded bg-warning/20 px-1.5 py-0.5 text-warning-dark">
            req
          </span>
        )}
        {filter.locked && (
          <span className="rounded bg-neutral-200 px-1.5 py-0.5 text-neutral-600">
            locked
          </span>
        )}
      </div>

      {supportsSelection && !filter.locked && (
        <div className="mb-3 space-y-2">
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={Boolean(filter.required)}
              aria-label={`${t('inspector.filter.required')}: ${filter.key}`}
              onChange={(e) => onChange({ required: e.target.checked })} />
            {t('inspector.filter.required')}
          </label>
          <Field label={t('inspector.filter.default')}>
            <input className="w-full rounded border p-1 text-xs"
              aria-label={`${t('inspector.filter.default')}: ${filter.key}`}
              value={filter.default_value ?? ''}
              onChange={(e) => onChange({ default_value: e.target.value || null })} />
          </Field>
          <Field label={t('inspector.filter.dependencies')}>
            <Select
              aria-label={`${t('inspector.filter.dependencies')}: ${filter.key}`}
              value={JSON.stringify(filter.depends_on ?? null)}
              options={[
                { value: 'null', label: t('inspector.filter.legacyCascade') },
                { value: '[]', label: t('inspector.filter.rootCascade') },
                ...preceding.map((_, i) => ({
                  value: JSON.stringify(preceding.slice(0, i + 1).map((f) => f.key)),
                  label: preceding.slice(0, i + 1).map((f) => f.key).join(' → '),
                })),
                // Preserve non-prefix DAGs authored via the API.
                ...(filter.depends_on?.length && !preceding.some((_, i) =>
                  JSON.stringify(preceding.slice(0, i + 1).map((f) => f.key)) === JSON.stringify(filter.depends_on)
                ) ? [{ value: JSON.stringify(filter.depends_on), label: filter.depends_on.join(' + ') }] : []),
              ]}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                onChange({ depends_on: JSON.parse(e.target.value) as string[] | null })}
              size="sm" fullWidth
            />
            <p className="mt-1 text-xs text-neutral-500">{t('inspector.filter.dependenciesHint')}</p>
          </Field>
          <Field label={t('inspector.filter.extraOptions')}>
            <input className="w-full rounded border p-1 text-xs"
              aria-label={`${t('inspector.filter.extraOptions')}: ${filter.key}`}
              key={JSON.stringify(filter.extra_options ?? [])}
              defaultValue={(filter.extra_options ?? []).join(', ')}
              onBlur={(e) => onChange({ extra_options: e.target.value.split(',').map(v => v.trim()).filter(Boolean) })} />
            <p className="mt-1 text-xs text-neutral-500">{t('inspector.filter.extraOptionsHint')}</p>
          </Field>
          <Field label={t('inspector.filter.selectionMode')}>
            <Select
              aria-label={`${t('inspector.filter.selectionMode')}: ${filter.key}`}
              value={filter.type ?? 'select'}
              options={[
                { value: 'select', label: t('editor.filter.typeSelect') },
                { value: 'autocomplete', label: t('editor.filter.typeAutocomplete') },
                { value: 'multi_select', label: t('inspector.filter.checkboxDropdown') },
              ]}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                try {
                  onChange(changeSelectionMode(filter, e.target.value as SelectionMode));
                  setModeError(false);
                } catch { setModeError(true); }
              }}
              size="sm"
              fullWidth
            />
          </Field>
          {modeError && <p role="alert" className="text-xs text-error-dark">{t('inspector.filter.multipleDefault')}</p>}
          {filter.options?.length ? (
            <div className="text-xs text-neutral-600">
              <p>{t('inspector.filter.fixedOptions', { count: filter.options.length })}</p>
              <button type="button" className="mt-1 underline" onClick={() => onChange({ options: [] })}>
                {t('inspector.filter.useLiveOptions')}
              </button>
            </div>
          ) : <p className="text-xs text-neutral-500">{t('inspector.filter.liveOptions')}</p>}
        </div>
      )}

      <Field label={t("editor.filter.labelLabel")}>
        <BilingualInput
          value={(filter.label as DisplayLabel) ?? ""}
          onChange={(next) => onChange({ label: next })}
          placeholder={t("editor.filter.labelPlaceholder")}
        />
      </Field>
      <div className="mt-2">
        <span className="block text-xs text-neutral-500">
          {t("editor.synonyms.title")}
        </span>
        <SynonymsEditor
          value={(filter.synonyms as Synonyms | undefined) ?? undefined}
          onChange={(next) => onChange({ synonyms: next })}
        />
      </div>

      <div className="mt-2 space-y-1">
        <StructuralRow
          label={t("inspector.filter.column")}
          value={filter.column}
          mono
        />
        {filter.default_value && (
          <StructuralRow
            label={t("inspector.filter.default")}
            value={filter.default_value}
            mono
            truncate
          />
        )}
        {filter.locked && (
          <StructuralRow
            label={t("inspector.filter.locked")}
            value={filter.locked}
            mono
          />
        )}
      </div>

      {filter.type === "date_range" && (
        <div className="mt-3 border-t border-neutral-200 pt-2">
          <DatePresetEditor
            presets={(filter.presets ?? []) as DatePreset[]}
            defaultPresetId={resolveDefaultPresetId(filter.default_value)}
            timezone={filter.timezone ?? null}
            onChange={(next) => onChange({ presets: next })}
          />
        </div>
      )}

      {filter.type === "range" && (
        <RangeFilterFields filter={filter} onChange={onChange} />
      )}

      {filter.type === "date_slider" && (
        <div className="mt-3 border-t border-neutral-200 pt-2">
          <Field label={t("inspector.filter.sliderStep")}>
            <Select
              options={[
                { value: "day", label: t("inspector.filter.sliderStep.day") },
                { value: "week", label: t("inspector.filter.sliderStep.week") },
                {
                  value: "month",
                  label: t("inspector.filter.sliderStep.month"),
                },
              ]}
              value={filter.slider_step ?? "day"}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                onChange({ slider_step: e.target.value })
              }
              size="sm"
              fullWidth
            />
          </Field>
          <p className="mt-1 text-xs text-neutral-500">
            {t("inspector.filter.sliderStepHint")}
          </p>
        </div>
      )}
    </li>
  );
}

/** A server snapshot, independent of pending edits, for report change reviews. */
function ReportSnapshot({ reportKey }: { reportKey: string }): JSX.Element {
  const { t } = useT();
  const [snapshot, setSnapshot] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  return <div className="space-y-2">
    <button type="button" disabled={loading} className="text-xs underline" onClick={async () => {
      setLoading(true);
      setError('');
      try {
        const context = await request<unknown>(`/api/builder/${encodeURIComponent(reportKey)}/context`);
        setSnapshot(JSON.stringify(context, null, 2));
      } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
      finally { setLoading(false); }
    }}>{t('inspector.dashboard.snapshot')}</button>
    {error && <p role="alert" className="text-xs text-error-dark">{error}</p>}
    {snapshot && <>
      <textarea aria-label={t('inspector.dashboard.snapshot')} readOnly value={snapshot} rows={5} className="w-full border p-2 font-mono text-xs" />
      <button type="button" className="text-xs underline" onClick={() => {
        const url = URL.createObjectURL(new Blob([snapshot], { type: 'application/json' }));
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `${reportKey}-snapshot-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
        anchor.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }}>{t('inspector.dashboard.downloadSnapshot')}</button>
    </>}
  </div>;
}

/**
 * Inspector fields of a numeric ``range`` filter: display format and the
 * default bounds. Bounds are typed in DISPLAY units (30 for 30 % on a
 * percent filter, like the portal widget) and stored in the column's
 * units through the render package's own ``encodeRange``, which also
 * sorts an inverted pair. Empty = that side open; both empty = no default.
 */
function RangeFilterFields({
  filter,
  onChange,
}: {
  filter: DashboardFilter;
  onChange: (diff: Partial<DashboardFilter>) => void;
}): JSX.Element {
  const { t } = useT();
  const scale = inputScale(filter.value_format);
  const current = decodeRange(filter.default_value ?? "");
  const shown = (v: number | null | undefined): string =>
    v == null ? "" : String(Number((v * scale).toPrecision(12)));
  // Controlled drafts, re-synced when the stored default or the format
  // changes (another edit, an undo, a percent/number switch).
  const [draftMin, setDraftMin] = useState(() => shown(current?.min));
  const [draftMax, setDraftMax] = useState(() => shown(current?.max));
  useEffect(() => {
    setDraftMin(shown(current?.min));
    setDraftMax(shown(current?.max));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.default_value, scale]);
  const commit = (side: "min" | "max", raw: string): void => {
    const typed = raw.trim() === "" ? null : Number(raw);
    if (typed !== null && !Number.isFinite(typed)) return;
    const next = typed === null ? null : Number((typed / scale).toPrecision(12));
    const encoded = encodeRange(
      side === "min" ? next : (current?.min ?? null),
      side === "max" ? next : (current?.max ?? null),
    );
    onChange({ default_value: encoded || null });
  };
  return (
    <div className="mt-3 space-y-2 border-t border-neutral-200 pt-2">
      <Field label={t("inspector.filter.rangeFormat")}>
        <Select
          aria-label={`${t("inspector.filter.rangeFormat")}: ${filter.key}`}
          options={[
            { value: "number", label: t("inspector.filter.rangeFormat.number") },
            { value: "percent", label: t("inspector.filter.rangeFormat.percent") },
            { value: "currency", label: t("inspector.filter.rangeFormat.currency") },
          ]}
          value={filter.value_format ?? "number"}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            onChange({ value_format: e.target.value })
          }
          size="sm"
          fullWidth
        />
      </Field>
      <div className="flex gap-2">
        <Field label={t("inspector.filter.rangeDefaultMin")}>
          <input
            type="number"
            className="w-full rounded border p-1 text-xs"
            aria-label={`${t("inspector.filter.rangeDefaultMin")}: ${filter.key}`}
            value={draftMin}
            onChange={(e) => setDraftMin(e.target.value)}
            onBlur={(e) => commit("min", e.target.value)}
          />
        </Field>
        <Field label={t("inspector.filter.rangeDefaultMax")}>
          <input
            type="number"
            className="w-full rounded border p-1 text-xs"
            aria-label={`${t("inspector.filter.rangeDefaultMax")}: ${filter.key}`}
            value={draftMax}
            onChange={(e) => setDraftMax(e.target.value)}
            onBlur={(e) => commit("max", e.target.value)}
          />
        </Field>
      </div>
      <Field label={t("inspector.filter.rangeScale")}>
        <Select
          aria-label={`${t("inspector.filter.rangeScale")}: ${filter.key}`}
          options={[
            { value: "linear", label: t("inspector.filter.rangeScale.linear") },
            { value: "log", label: t("inspector.filter.rangeScale.log") },
          ]}
          value={filter.slider_scale ?? "linear"}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            onChange({ slider_scale: e.target.value })
          }
          size="sm"
          fullWidth
        />
      </Field>
      <Field label={t("inspector.filter.rangeHistogramTable")}>
        <input
          className="w-full rounded border p-1 text-xs"
          aria-label={`${t("inspector.filter.rangeHistogramTable")}: ${filter.key}`}
          value={filter.histogram_table ?? ""}
          placeholder="tbl_main"
          onChange={(e) => onChange({ histogram_table: e.target.value.trim() || null })}
        />
      </Field>
      <p className="text-xs text-neutral-500">{t("inspector.filter.rangeHint")}</p>
    </div>
  );
}

/** Parse ``default_value`` JSON to extract the preset id when the
 * default points at a named preset (e.g. ``{"preset": "hp_last_4_q"}``).
 * Used for the "⚠ default" badge on the matching preset card.
 * Returns ``null`` for fixed-date or relative defaults. */
function resolveDefaultPresetId(
  raw: string | null | undefined,
): string | null {
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw) as { preset?: unknown };
    return typeof obj.preset === "string" ? obj.preset : null;
  } catch {
    return null;
  }
}

function SectionLabel({ text }: { text: string }): JSX.Element {
  return (
    <div className="mb-1 border-b border-neutral-200 pb-1">
      <span className="text-xs font-semibold text-neutral-600">
        {text}
      </span>
    </div>
  );
}
